import React, { useState, useEffect } from "react";
import { Box, Grid, Typography, Card, CardContent, useTheme, IconButton, useMediaQuery } from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";

// Icons
import BusinessIcon from "@mui/icons-material/Business";
import EventNoteIcon from "@mui/icons-material/EventNote";
import GroupIcon from "@mui/icons-material/Group";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BuildIcon from "@mui/icons-material/Build";
import ChatIcon from "@mui/icons-material/Chat";
import DataThresholdingIcon from "@mui/icons-material/DataThresholding";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SmartToyIcon from '@mui/icons-material/SmartToy';

// Redesigned Minimalist AnalogClock Component
const AnalogClock = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  const hours = time.getHours() % 12;
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();

  const hourRotation = 30 * hours + minutes / 2;
  const minuteRotation = 6 * minutes;
  const secondRotation = 6 * seconds;

  const isDark = theme.palette.mode === "dark";
  const primaryColor = isDark ? colors.blueAccent[300] : colors.blueAccent[500];
  const secondaryColor = isDark ? colors.blueAccent[200] : colors.blueAccent[400];
  const accentColor = isDark ? colors.greenAccent[400] : colors.greenAccent[500];
  const backgroundColor = isDark ? colors.primary[400] : "#ffffff";
  const textColor = isDark ? colors.grey[100] : colors.grey[900];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        paddingBottom: "100%",
        borderRadius: "50%",
        bgcolor: backgroundColor,
        boxShadow: isDark
          ? "0 8px 24px rgba(0, 0, 0, 0.2)"
          : "0 8px 24px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
      }}
    >
      {/* Subtle background pattern */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          opacity: 0.05,
          backgroundImage: `radial-gradient(circle, ${primaryColor} 1px, transparent 1px)`,
          backgroundSize: "10px 10px",
        }}
      />

      {/* Clock face */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Minimalist hour markers */}
        {Array.from({ length: 12 }).map((_, i) => (
          <Box
            key={`hour-${i}`}
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              transform: `rotate(${i * 30}deg)`,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "8%",
                left: "49.5%",
                width: i % 3 === 0 ? "2%" : "1%",
                height: i % 3 === 0 ? "4%" : "2%",
                backgroundColor: i % 3 === 0 ? primaryColor : secondaryColor,
                borderRadius: "4px",
                opacity: i % 3 === 0 ? 1 : 0.7,
              }}
            />
          </Box>
        ))}

        {/* Minimal numeric indicators for main hours */}
        {[12, 3, 6, 9].map((number) => {
          return (
            <Typography
              key={number}
              variant="h6"
              fontWeight="600"
              sx={{
                position: "absolute",
                top: number === 12 ? "15%" : number === 6 ? "78%" : "50%",
                left: number === 3 ? "78%" : number === 9 ? "15%" : "50%",
                transform: "translate(-50%, -50%)",
                color: primaryColor,
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
              }}
            >
              {number === 0 ? 12 : number}
            </Typography>
          );
        })}

        {/* Clock hands */}
        {/* Hour hand */}
        <Box
          sx={{
            position: "absolute",
            bottom: "50%",
            left: "50%",
            width: "4px",
            height: "25%",
            backgroundColor: primaryColor,
            transformOrigin: "bottom",
            transform: `translateX(-50%) rotate(${hourRotation}deg)`,
            borderRadius: "6px 6px 2px 2px",
            zIndex: 2,
          }}
        />

        {/* Minute hand */}
        <Box
          sx={{
            position: "absolute",
            bottom: "50%",
            left: "50%",
            width: "3px",
            height: "38%",
            backgroundColor: secondaryColor,
            transformOrigin: "bottom",
            transform: `translateX(-50%) rotate(${minuteRotation}deg)`,
            borderRadius: "6px 6px 1px 1px",
            zIndex: 3,
          }}
        />

        {/* Second hand */}
        <Box
          sx={{
            position: "absolute",
            bottom: "50%",
            left: "50%",
            width: "1.5px",
            height: "45%",
            backgroundColor: accentColor,
            transformOrigin: "bottom",
            transform: `translateX(-50%) rotate(${secondRotation}deg)`,
            borderRadius: "2px",
            zIndex: 4,
          }}
        />

        {/* Center dot with subtle detail */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "12px",
            height: "12px",
            backgroundColor: isDark ? colors.grey[300] : "#ffffff",
            border: `2px solid ${primaryColor}`,
            borderRadius: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 5,
            boxShadow: `0 0 0 2px ${backgroundColor}, 0 0 0 3px ${secondaryColor}`,
          }}
        />

        {/* Digital time display */}
        <Box
          sx={{
            position: "absolute",
            bottom: "25%",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "6px 12px",
            borderRadius: "20px",
            backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "rgba(173, 173, 173, 0.5)",
            backdropFilter: "blur(4px)",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Roboto Mono', monospace",
              fontWeight: "500",
              letterSpacing: "1px",
            }}
          >
            {time.toLocaleTimeString("de-DE")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Redesigned Minimalist Calendar Component
const GermanCalendar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [date, setDate] = useState(new Date());
  const [holidays, setHolidays] = useState([]);

  // Schleswig-Holstein holidays 2025
  const schleswigHolsteinHolidays = [
    { date: "2025-01-01", name: "Neujahr" },
    { date: "2025-04-18", name: "Karfreitag" },
    { date: "2025-04-21", name: "Ostermontag" },
    { date: "2025-05-01", name: "Tag der Arbeit" },
    { date: "2025-05-29", name: "Christi Himmelfahrt" },
    { date: "2025-06-09", name: "Pfingstmontag" },
    { date: "2025-10-03", name: "Tag der Deutschen Einheit" },
    { date: "2025-12-25", name: "1. Weihnachtstag" },
    { date: "2025-12-26", name: "2. Weihnachtstag" },
  ];

  useEffect(() => {
    setHolidays(schleswigHolsteinHolidays);
  }, []);

  // Get the number of days in the current month
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

  // Get the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  // Adjust to Monday as first day of week (European standard)
  const adjustDay = (day) => (day === 0 ? 6 : day - 1);

  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayAdjusted = adjustDay(getFirstDayOfMonth(year, month));
  
  // German month names
  const monthNames = [
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember"
  ];

  // German day names (starting with Monday)
  const dayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

  const goToPreviousMonth = () => {
    setDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setDate(new Date(year, month + 1, 1));
  };

  // Check if a date is a holiday
  const isHoliday = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return holidays.find(holiday => holiday.date === dateStr);
  };

  // Generate calendar days
  const days = [];
  for (let i = 0; i < firstDayAdjusted; i++) {
    days.push(null); // Empty cells for days before the 1st of the month
  }
  
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  const isDark = theme.palette.mode === "dark";
  const primaryColor = isDark ? colors.blueAccent[300] : colors.blueAccent[500];
  const secondaryColor = isDark ? colors.grey[100] : colors.grey[900]; // Fixed for dark mode
  const holidayColor = isDark ? colors.redAccent[400] : colors.redAccent[500];
  const todayBgColor = isDark ? colors.blueAccent[700] : colors.blueAccent[100];
  const todayColor = isDark ? colors.grey[100] : colors.blueAccent[700];
  const holidayBgColor = isDark ? "rgba(219, 67, 67, 0.15)" : "rgba(219, 67, 67, 0.08)";
  const weekendColor = isDark ? colors.grey[400] : colors.grey[700];
  
  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        borderRadius: "12px",
        bgcolor: isDark ? colors.primary[500] : "#ffffff",
        boxShadow: isDark 
          ? "0 8px 24px rgba(0, 0, 0, 0.2)" 
          : "0 8px 24px rgba(0, 0, 0, 0.08)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Calendar header with title */}
      <Typography
        variant="h5"
        fontWeight="600"
        sx={{ 
          mb: 2,
          letterSpacing: "0.5px",
        }}
      >
        Kalender - Schleswig-Holstein
      </Typography>

      {/* Month/Year navigation */}
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          mb: 3,
          pb: 2,
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
        }}
      >
        <IconButton 
          onClick={goToPreviousMonth}
          sx={{ 
            color: primaryColor,
            "&:hover": { 
              backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" 
            }
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography 
          variant="h4" 
          fontWeight="500"
          sx={{
            letterSpacing: "0.5px",
          }}
        >
          {monthNames[month]} {year}
        </Typography>
        <IconButton 
          onClick={goToNextMonth}
          sx={{ 
            color: primaryColor,
            "&:hover": { 
              backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" 
            }
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Calendar weekdays */}
      <Grid container spacing={0} sx={{ mb: 1 }}>
        {dayNames.map((day, index) => (
          <Grid item xs={12/7} key={day}>
            <Typography 
              variant="subtitle2" 
              align="center"
              fontWeight="600"
              sx={{ 
                color: index >= 5 ? weekendColor : primaryColor,
                fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                letterSpacing: "1px",
                mb: 1,
              }}
            >
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar days */}
      {/* <Grid container spacing={0} sx={{ flexGrow: 1 }}>
        {days.map((day, index) => {
          const isCurrentDay = day === new Date().getDate() && 
                              month === new Date().getMonth() && 
                              year === new Date().getFullYear();
          
          const holiday = day ? isHoliday(day) : null;
          const isWeekend = (index + firstDayAdjusted) % 7 >= 5;
          
          return (
            <Grid item xs={12/7} key={index}>
              <Box
                sx={{
                  aspectRatio: "1/1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  m: { xs: 0.2, sm: 0.5 },
                  borderRadius: "8px",
                  transition: "all 0.2s ease",
                  ...(isCurrentDay && {
                    bgcolor: todayBgColor,
                    color: todayColor,
                    boxShadow: `0 2px 8px ${isDark ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)"}`,
                  }),
                  ...(holiday && {
                    bgcolor: holidayBgColor,
                    color: holidayColor,
                  }),
                  ...(isWeekend && !isCurrentDay && !holiday && {
                    color: weekendColor,
                  }),
                  ...(day && {
                    "&:hover": {
                      bgcolor: isCurrentDay ? todayBgColor : 
                              (holiday ? holidayBgColor : isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"),
                      transform: "translateY(-2px)",
                      cursor: "pointer",
                    }
                  })
                }}
              >
                {day && (
                  <>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontWeight: isCurrentDay ? "600" : (holiday ? "500" : "400"),
                        fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                        color: "inherit", // Inherit color from parent Box
                      }}
                    >
                      {day}
                    </Typography>
                    {holiday && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: "15%",
                          width: "24px",
                          height: "3px",
                          borderRadius: "2px",
                          backgroundColor: holidayColor,
                          opacity: 0.8,
                        }}
                      />
                    )}
                  </>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid> */}

      {/* Holiday legend */}
      <Box 
        sx={{ 
          mt: 2, 
          pt: 2, 
          borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box 
          sx={{ 
            width: "12px", 
            height: "12px", 
            borderRadius: "2px", 
            backgroundColor: holidayColor,
            mr: 1 
          }}
        />
        <Typography 
          variant="caption" 
          sx={{ 
            color: isDark ? colors.grey[300] : colors.grey[700],
            fontSize: "0.75rem" 
          }}
        >
          Feiertage in Schleswig-Holstein
        </Typography>
      </Box>
    </Box>
  );
};

// Redesigned Feature Card Component
const FeatureCard = ({ title, icon, color, bgColor, path, description }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  
  return (
    <Card 
      sx={{ 
        height: "100%", 
        display: "flex", 
        flexDirection: "column",
        borderRadius: "12px",
        transition: "all 0.3s ease",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
        backgroundColor: isDark ? colors.primary[500] : "#ffffff",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: isDark 
            ? "0 12px 24px rgba(0, 0, 0, 0.25)" 
            : "0 12px 24px rgba(0, 0, 0, 0.1)",
          cursor: "pointer",
        },
        overflow: "hidden",
      }}
      onClick={() => navigate(path)}
    >
      <Box 
        sx={{ 
          bgcolor: "transparent", 
          pt: 3,
          pb: 1,
          px: 2,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box 
          sx={{ 
            bgcolor: bgColor,
            width: "56px",
            height: "56px",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: isDark 
              ? "0 4px 8px rgba(0, 0, 0, 0.25)" 
              : "0 4px 8px rgba(0, 0, 0, 0.08)",
          }}
        >
          {React.cloneElement(icon, { 
            sx: { 
              fontSize: 24, 
              color: color,
            } 
          })}
        </Box>
        <Typography 
          variant="h6" 
          fontWeight="500" 
          sx={{ 
            ml: 2
          }}
        >
          {title}
        </Typography>
      </Box>
      <CardContent sx={{ flexGrow: 1, pt: 1, pb: 2, px: 2 }}>
        <Typography 
          variant="body2"
          sx={{ 
            color: isDark ? colors.grey[300] : colors.grey[600],
            fontSize: "0.85rem",
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [currentDate, setCurrentDate] = useState(new Date());
  const isDark = theme.palette.mode === "dark";
  
  // Update current date every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  // Feature data
  const features = [
    // {
    //   title: "KI-Assistent",
    //   icon: <SmartToyIcon />,
    //   color: colors.greenAccent[500],
    //   bgColor: isDark ? colors.greenAccent[900] : colors.greenAccent[100],
    //   path: "/ai-assistant",
    //   description: "Intelligente Analyse und Datenauswertung"
    // },
    {
      title: "Projekte",
      icon: <BusinessIcon />,
      color: colors.blueAccent[500],
      bgColor: isDark ? colors.blueAccent[900] : colors.blueAccent[100],
      path: "/anrTable",
      description: "Projektverwaltung und Übersicht"
    },
    {
      title: "Aufgaben",
      icon: <AssignmentIcon />,
      color: colors.redAccent[500],
      bgColor: isDark ? colors.redAccent[900] : colors.redAccent[100],
      path: "/MainAufgabeBoard",
      description: "Task-Management und Kanban Board"
    },
    {
      title: "Personal",
      icon: <GroupIcon />,
      color: colors.redAccent[500],
      bgColor: isDark ? colors.redAccent[900] : colors.redAccent[100],
      path: "/team",
      description: "Mitarbeiterverwaltung und Anwesenheit"
    },
    {
      title: "Maschinen",
      icon: <BuildIcon />,
      color: colors.blueAccent[500],
      bgColor: isDark ? colors.blueAccent[900] : colors.blueAccent[100],
      path: "/matrix",
      description: "Maschinenverwaltung und Fertigungsplanung"
    },
    {
      title: "Planung",
      icon: <CalendarMonthIcon />,
      color: colors.blueAccent[500],
      bgColor: isDark ? colors.blueAccent[900] : colors.blueAccent[100],
      path: "/planning",
      description: "Projektplanung und Zeitmanagement"
    },
    {
      title: "Rechnungen",
      icon: <ReceiptIcon />,
      color: colors.blueAccent[400],
      bgColor: isDark ? colors.blueAccent[900] : colors.blueAccent[100],
      path: "/invoices",
      description: "Rechnungsverwaltung und Finanzen"
    },
    {
      title: "Berichte",
      icon: <DataThresholdingIcon />,
      color: colors.redAccent[500],
      bgColor: isDark ? colors.redAccent[900] : colors.redAccent[100],
      path: "/reports",
      description: "Projektberichte und Auswertungen"
    },
    {
      title: "Lager",
      icon: <InventoryIcon />,
      color: colors.greenAccent[500],
      bgColor: isDark ? colors.greenAccent[900] : colors.greenAccent[100],
      path: "/stock",
      description: "Materialverwaltung und Bestellungen"
    },
    {
      title: "Reklamationen",
      icon: <ReportProblemIcon />,
      color: colors.redAccent[500],
      bgColor: isDark ? colors.redAccent[900] : colors.redAccent[100],
      path: "/reklamation",
      description: "Reklamationsverwaltung und -bearbeitung"
    },
    {
      title: "Chat",
      icon: <ChatIcon />,
      color: colors.primary[400],
      bgColor: isDark ? colors.primary[900] : colors.primary[100],
      path: "/chat",
      description: "Kommunikation im Team"
    },
    {
      title: "Urlaub",
      icon: <EventNoteIcon />,
      color: colors.greenAccent[500],
      bgColor: isDark ? colors.greenAccent[900] : colors.greenAccent[100],
      path: "/holiday",
      description: "Urlaubsplanung und -verwaltung"
    },
    {
      title: "Aufgabenliste",
      icon: <FormatListBulletedIcon />,
      color: colors.blueAccent[500],
      bgColor: isDark ? colors.blueAccent[900] : colors.blueAccent[100],
      path: "/tasks",
      description: "Aufgabenliste und To-Dos"
    },
  ];

  // Format date for German locale
  const formattedDate = currentDate.toLocaleDateString('de-DE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: "1600px",
        margin: "0 auto",
      }}
    >
      {/* HEADER */}
      <Box 
        display="flex" 
        flexDirection={{ xs: "column", sm: "row" }} 
        justifyContent="space-between" 
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{
          mb: 4,
          pb: 3,
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
        }}
      >
        <Header 
          title="DASHBOARD" 
          subtitle="Willkommen zu Ihrem CNC Heidenau Dashboard" 
          titleProps={{ 
            fontWeight: "600", 
            letterSpacing: "1px",
            color: isDark ? colors.grey[100] : colors.grey[900],
          }}
          subtitleProps={{
            fontSize: "0.95rem",
            color: isDark ? colors.grey[400] : colors.grey[600],
            mt: 0.5,
          }}
        />
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mt: { xs: 2, sm: 0 },
            p: 1.5,
            borderRadius: "8px",
            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
          }}
        >
          <WatchLaterIcon sx={{ mr: 1.5, color: isDark ? colors.grey[400] : colors.grey[500] }} />
          <Typography 
            variant="h6"
            fontWeight="500"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
          >
            {formattedDate}
          </Typography>
        </Box>
      </Box>

      {/* GRID & COMPONENTS */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="auto"
        gap="24px"
      >
        {/* Main Content Layout */}

        {/* Feature Grid (Now on Right) */}
        <Box
          gridColumn={{ xs: "span 12", md: "span 12", lg: "span 12" }}
          gridRow="span 1"
        >
          <Typography
            variant="h5"
            fontWeight="600"
            sx={{ 
              mb: 3, 
              letterSpacing: "0.5px",
            }}
          >
            App-Funktionen
          </Typography>
          <Grid container spacing={8}>
            {features.map((feature, index) => (
              <Grid item size={8} xs={12} sm={6} md={6} lg={3} key={index}>
                <FeatureCard
                  title={feature.title}
                  icon={feature.icon}
                  color={feature.color}
                  bgColor={feature.bgColor}
                  path={feature.path}
                  description={feature.description}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;